import React, { useState, useEffect, useContext, useMemo } from "react";
import { serverHost, createEndpoints } from "variables/serverdata";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
} from "react-bootstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";
import HusariaHeader from "views/pages/components/HusariaHeader";

const VoicemailAddEdit = ({ method }) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const { id } = useParams();
  const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
  const site_uuid = currentSite?.site_uuid || null;
  const ivr_other = [
    { value: "menu-top:", name: "top" },
    { value: "menu-back:", name: "back" },
    { value: "menu-exit:", name: "exit" },
  ];
  const dialplans = [
    { value: "transfer:*98 XML 10.0.190.48", label: "Check Voicemail" },
    { value: "transfer:*411 XML 10.0.190.48", label: "Company Directory" },
    { value: "hangup:", label: "Hangup" },
    { value: "transfer:*732 XML 10.0.190.48", label: "Record" },
  ];
  const [formData, setFormData] = useState({
    voicemail_id: "",
    password: "",
    play_tutorial: false,
    greeting: null,
    alternate_greeting_id: null,
    recording_instructions: true,
    recording_options: true,

    transcription_enabled: false,
    mail_to: null,
    voicemail_file: null,
    keep_local_after_email: false,
    forward_destination: null,
    enabled: true,
    description: null,
  });

  const [options, setOptions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  // Data lists
  const [extensionList, setExtensionList] = useState([]);
  const [bridgeList, setBridgeList] = useState([]);
  const [voicemailList, setVoicemailList] = useState([]);
  const [flowsList, setFlowsList] = useState([]);
  const [ivrList, setIVRList] = useState([]);
  const [timeConditionsList, setTCList] = useState([]);
  const [destinationsList, setDestList] = useState([]);
  const [ringGroupList, setRingGroup] = useState([]);
  const [tonesList, setTonesList] = useState([]);
  const [domainsList, setDomainList] = useState([]);
  const greetingOptions = [
    { value: "", label: "Select..." },
    { value: "default", label: "Default" },
    { value: "none", label: "None" },
  ];

  const voicemailFileOptions = [
    { value: "", label: "Listen Link (Login Required)" },
    { value: "link", label: "Download Link (No Login Required)" },
    { value: "attach", label: "Audio File Attachment" },
  ];

  const endpoints = useMemo(
    () => ({
      ...createEndpoints({
        setExtensionList,
        setBridgeList,
        setVoicemailList,
        setFlowsList,
        setIVRList,
        setTCList,
        setDestList,
        setRingGroup,
        setDomainList,
        site_uuid,
      }),

      tones: {
        url: `${serverHost}/get_rows?table=v_vars&pageSize=50&page=1&cfilter=var_category&vfilter=Tones&domain_uuid=${encodeURIComponent(site_uuid)}`,
        map: (r) => ({
          value: r.var_value,
          name: r.var_name,
        }),
        setter: setTonesList,
      },
    }),
    [
      site_uuid,
      serverHost,
      setExtensionList,
      setBridgeList,
      setVoicemailList,
      setFlowsList,
      setIVRList,
      setTCList,
      setDestList,
      setRingGroup,
      setDomainList,
      setTonesList,
    ],
  );

  useEffect(() => {
    const fetchData = async () => {
      for (const key in endpoints) {
        const { url, map, setter } = endpoints[key];
        try {
          const res = await fetch(url);
          if (!res.ok) continue;

          const data = await res.json();
          const mapped = data.results.map(map);
          setter(mapped);
        } catch (err) {
          console.error(`Failed to fetch ${key}:`, err);
        }
      }
    };

    const fetchVoicemail = async () => {
      try {
        let res = await fetch(
          `${serverHost}/fetch_voicemail/${encodeURIComponent(id)}`,
        );
        if (!res.ok)
          throw new Error(
            res.status === 404 ? "Row not found" : "Failed to fetch row",
          );
        let data = await res.json();
        if (!data?.voicemail) throw new Error("Row not found");

        const vm = data.voicemail;

        setFormData({
          voicemail_id: vm.voicemail_id || "",
          password: vm.voicemail_password || "",
          play_tutorial: vm.voicemail_tutorial === "true",
          greeting: vm.greeting_id || "",
          alternate_greeting_id: vm.voicemail_alternate_greet_id || null,
          recording_instructions:
            vm.voicemail_recording_instructions !== "false",
          recording_options: vm.voicemail_recording_options !== "false",
          transcription_enabled: vm.voicemail_transcription_enabled === "true",
          mail_to: vm.voicemail_mail_to || null,
          voicemail_file: vm.voicemail_file || null,
          keep_local_after_email: vm.voicemail_local_after_email === "true",
          forward_destination: vm.voicemail_forward_destination || null,
          enabled: vm.voicemail_enabled !== "false",
          description: vm.voicemail_description || null,
        });

        setOptions(data.options);
      } catch (err) {
        console.log("Failed to fetch voicemail:", err);
        setError("Failed to fetch voicemail: " + err.message);
      }
    };

    fetchData();
    if (method === "edit") {
      fetchVoicemail();
    }
  }, [method, id]);

  const labelStyle = {
    fontWeight: 600,
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: "0.5rem",
  };

  const inputStyle = {
    backgroundColor: isDark ? "#374151" : "#fff",
    color: isDark ? "#e2e8f0" : "#495057",
    borderColor: isDark ? "#4b5563" : "#ced4da",
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    setOptions(newOptions);
  };

  const addOption = () => {
    const lastOrder =
      options.length > 0 ? Math.max(...options.map((o) => o.order)) : 0;
    setOptions([
      ...options,
      { option_digit: null, destination: null, order: null, description: null },
    ]);
  };

  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const renderDestinationDropdown = (value, onChange) => (
    <Input type="select" value={value} onChange={onChange} style={inputStyle}>
      <option value="">Select...</option>
      <optgroup label="Bridges">
        {bridgeList.map((br) => (
          <option
            key={br.bridge_uuid}
            value={`menu-exec-app:bridge ${br.destination}`}
          >
            {br.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Call Flows">
        {flowsList.map((cf) => (
          <option key={cf.name} value={`menu-exec-app:transfer ${cf.value}`}>
            {cf.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Destinations">
        {destinationsList.map((d) => (
          <option key={d.value} value={`menu-exec-app:transfer ${d.value}`}>
            {d.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Extensions">
        {extensionList.map((ext) => (
          <option
            key={ext.extension_uuid}
            value={`menu-exec-app:transfer ${ext.name} XML ${ext.accountcode}`}
          >
            {ext.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="IVR Menus">
        {ivrList.map((ivr) => (
          <option
            key={ivr.name}
            value={`menu-exec-app:transfer ${ivr.transfer}`}
          >
            {ivr.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="IVR Menus - Others">
        {ivr_other.map((ivr) => (
          <option key={ivr.name} value={ivr.value}>
            {ivr.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="IVR Menus - Sub">
        {ivrList.map((ivr) => (
          <option key={ivr.name} value={`menu-sub:${ivr.value}`}>
            {ivr.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Ring Groups">
        {ringGroupList.map((rg) => (
          <option key={rg.value} value={`menu-exec-app:transfer ${rg.value}`}>
            {rg.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Time Conditions">
        {timeConditionsList.map((tc) => (
          <option key={tc.name} value={`menu-exec-app:transfer ${tc.value}`}>
            {tc.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Tones">
        {tonesList.map((tone) => (
          <option
            key={tone.name}
            value={`menu-exec-app:playback tone_stream://${tone.value}`}
          >
            {tone.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Voicemails">
        {voicemailList.map((vm) => (
          <option
            key={vm.voicemail_uuid}
            value={`menu-exec-app:transfer ${vm.value}`}
          >
            {vm.voicemail_id}
          </option>
        ))}
      </optgroup>
      <optgroup label="Dialplans">
        {dialplans.map((dp) => (
          <option key={dp.value} value={`menu-exec-app:${dp.value}`}>
            {dp.label}
          </option>
        ))}
      </optgroup>
    </Input>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const missing = [];
    if (!formData.voicemail_id) missing.push("Voicemail ID");
    if (!formData.password) missing.push("Password");

    if (missing.length > 0) {
      setError("Please fill in missing fields: " + missing.join(", "));
      return;
    }

    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      let url_id = site.site_uuid;
      const payload = {
        data: formData,
        option: options,
        client_uuid: site.client_uuid,
        site_name: site.company_name,
        insert_uuid: JSON.parse(localStorage.getItem("user")).user_uuid,
      };

      let endpoint = "voicemail";
      if (method === "edit") {
        endpoint = "edit_voicemail";
        payload.site_uuid = site.site_uuid;
        url_id = id;
      }

      const response = await fetch(`${serverHost}/${endpoint}/${url_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate("/admin/hucs/voicemails");
      } else {
        const error = await response.json();
        console.error("Failed to save voicemail:", error);
        setError("Failed to save voicemail");
      }
    } catch (error) {
      console.error("Error saving voicemail:", error);
      setError("Error saving voicemail: " + error.message);
    }
  };

  const handleCancel = () => {
    navigate("/admin/hucs/voicemails");
  };

  return (
    <>
      <HusariaHeader
        name={`${method.toUpperCase()} Voicemail`}
        parentName="HUCS"
        padding="4.5rem"
      />
      <Container fluid className="mt--6">
        <Card className="shadow-sm" style={{ border: "none" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              {method.toUpperCase()} VOICEMAIL
            </h5>
          </Card.Header>

          <Card.Body
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              padding: "1.5rem",
            }}
          >
            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Voicemail ID <span style={{ color: "#dc3545" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="voicemail_id"
                      value={formData.voicemail_id}
                      onChange={handleChange}
                      placeholder="Enter voicemail ID"
                      required
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Password <span style={{ color: "#dc3545" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password"
                      required
                      style={inputStyle}
                      onMouseEnter={() => setShowPassword(true)}
                      onMouseLeave={() => setShowPassword(false)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Greeting Settings */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Greeting</Form.Label>
                    <Input
                      type="select"
                      name="greeting"
                      value={formData.greeting}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      {greetingOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Alternate Greeting ID
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="alternate_greeting_id"
                      value={formData.alternate_greeting_id}
                      onChange={handleChange}
                      placeholder="Enter alternate greeting ID"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Toggles */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="play_tutorial"
                      name="play_tutorial"
                      label="Play Tutorial"
                      checked={formData.play_tutorial}
                      onChange={handleChange}
                      style={{ color: isDark ? "#e2e8f0" : "#495057" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="recording_instructions"
                      name="recording_instructions"
                      label="Recording Instructions"
                      checked={formData.recording_instructions}
                      onChange={handleChange}
                      style={{ color: isDark ? "#e2e8f0" : "#495057" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="recording_options"
                      name="recording_options"
                      label="Recording Options"
                      checked={formData.recording_options}
                      onChange={handleChange}
                      style={{ color: isDark ? "#e2e8f0" : "#495057" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="transcription_enabled"
                      name="transcription_enabled"
                      label="Transcription Enabled"
                      checked={formData.transcription_enabled}
                      onChange={handleChange}
                      style={{ color: isDark ? "#e2e8f0" : "#495057" }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Options Section */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label style={{ ...labelStyle, marginBottom: 0 }}>
                    Options
                  </Form.Label>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addOption}
                    style={{
                      borderColor: "#5469d4",
                      color: "#5469d4",
                    }}
                  >
                    + Add Option
                  </Button>
                </div>

                {options.map((option, index) => (
                  <Card
                    key={index}
                    className="mb-2"
                    style={{
                      backgroundColor: isDark ? "#374151" : "#f8f9fa",
                      border: `1px solid ${isDark ? "#4b5563" : "#dee2e6"}`,
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small
                          style={{
                            color: isDark ? "#9ca3af" : "#6c757d",
                            fontWeight: 600,
                          }}
                        >
                          Option {index + 1}
                        </small>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="text-danger"
                          onClick={() => removeOption(index)}
                        >
                          Remove Option
                        </Button>
                      </div>

                      <Row className="mb-2">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Option Text
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={option.option_digit}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "option_digit",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter option text"
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>

                        <Col md={1}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Order
                            </Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={option.order}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "order",
                                  e.target.value,
                                )
                              }
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Destination
                            </Form.Label>
                            {renderDestinationDropdown(
                              option.destination,
                              (e) =>
                                handleOptionChange(
                                  index,
                                  "destination",
                                  e.target.value,
                                ),
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label
                              style={{ ...labelStyle, fontSize: "0.875rem" }}
                            >
                              Description
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={option.description}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter description"
                              style={inputStyle}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {/* Email Settings */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Mail To</Form.Label>
                    <Form.Control
                      type="email"
                      name="mail_to"
                      value={formData.mail_to}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Voicemail File</Form.Label>
                    <Input
                      type="select"
                      name="voicemail_file"
                      value={formData.voicemail_file}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      {voicemailFileOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="keep-local-after-email"
                      name="keep_local_after_email"
                      label="Keep Local After Email"
                      checked={formData.keep_local_after_email}
                      onChange={handleChange}
                      style={{ color: isDark ? "#e2e8f0" : "#495057" }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Forward Destination */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Forward Destination
                    </Form.Label>
                    <Input
                      type="select"
                      value={formData.forward_destination}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select...</option>
                      {extensionList.map((ext) => (
                        <option
                          key={ext.extension_uuid}
                          value={ext.extension_uuid}
                        >
                          {ext.name}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              {/* Enabled and Description */}
              <Row className="mb-3">
                <Col md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                      Enabled
                    </span>

                    <label className="custom-toggle mb-0">
                      <input
                        type="checkbox"
                        id="enabled-checkbox"
                        name="enabled"
                        checked={formData.enabled}
                        onChange={handleChange}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="Off"
                        data-label-on="On"
                      />
                    </label>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Enter description"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between mt-4">
                <Button variant="danger" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" variant="success">
                  {method.toUpperCase()} Voicemail
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default VoicemailAddEdit;
